/**
 * Top Accounts Table
 *
 * Table showing top accounts by revenue.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Link } from 'react-router-dom';
import type { TopAccount } from '../types';

interface TopAccountsTableProps {
  data?: TopAccount[];
  loading?: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function TopAccountsTable({ data, loading }: TopAccountsTableProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Top Accounts by Revenue</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse flex gap-4">
                <div className="h-5 bg-muted rounded w-16" />
                <div className="h-5 bg-muted rounded flex-1" />
                <div className="h-5 bg-muted rounded w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasData = data && data.length > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Top Accounts by Revenue</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {hasData ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Account</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Pending</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((account) => (
                <TableRow key={account.account_id}>
                  <TableCell className="font-mono text-xs">
                    <Link
                      to={`/accounts/${account.account_id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {account.account_number}
                    </Link>
                  </TableCell>
                  <TableCell className="font-medium truncate max-w-[200px]">
                    {account.account_name}
                  </TableCell>
                  <TableCell className="text-right text-green-600">
                    {formatCurrency(account.total_paid)}
                  </TableCell>
                  <TableCell className="text-right text-amber-600">
                    {formatCurrency(account.total_pending)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
            No account data available
          </div>
        )}
      </CardContent>
    </Card>
  );
}
